import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Eye, ThumbsUp, ExternalLink, Loader2, ListPlus, Headphones } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import { AdBanner } from '../components/GoogleAd';
import InlineYouTubePlayer from '../components/InlineYouTubePlayer';
import { useAudio } from '../contexts/AudioContext';
import { useQueue } from '../contexts/QueueContext';
import { applyPlayBlockHandoff, createPlaybackHandoff } from '../lib/playbackHandoff';
import { buildVideoEntity } from '../lib/videoProgress';
import { recordVideoRoute } from '../lib/recordRoutes';
import { toast } from 'sonner';
import ShareButton from '../components/ShareButton';
import { formatVideoViews, videoViewCount } from '../lib/videoMetrics';

const API = process.env.REACT_APP_BACKEND_URL;

const VideoDetailPage = () => {
  const { videoId } = useParams();
  const location = useLocation();
  const [video, setVideo] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [handoffPending, setHandoffPending] = useState(false);
  const inlinePlayingRef = useRef(false);
  const inlinePlayerRef = useRef(null);
  const { playYouTube, stopAll, ytPlayerRef } = useAudio();
  const { addToQueue } = useQueue();

  const isVideoMix = location.pathname.startsWith('/video-mixes');
  const backPath = isVideoMix ? '/video-mixes' : '/records';
  const backLabel = isVideoMix ? 'Video Mixes' : 'Rastyle Records';
  const entity = useMemo(
    () => buildVideoEntity(video || {}, videoId, isVideoMix ? 'video_mix' : 'video'),
    [isVideoMix, video, videoId],
  );
  const metadata = useMemo(() => ({
    channel_title: video?.channel_title,
    route: location.pathname,
    video_kind: isVideoMix ? 'video_mix' : 'record',
  }), [isVideoMix, location.pathname, video?.channel_title]);

  const fetchVideo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/youtube/videos/${videoId}`);
      setVideo(res.data.video);
      setRelated(res.data.related || []);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Video not found' : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  useEffect(() => { fetchVideo(); }, [fetchVideo]);

  useEffect(() => {
    if (video) stopAll();
  }, [stopAll, video]);

  const handleInlinePlayingChange = useCallback((playing) => {
    inlinePlayingRef.current = playing;
  }, []);

  const handlePlayInPlayer = async () => {
    if (!video || handoffPending) return;
    setHandoffPending(true);

    const sample = inlinePlayerRef.current?.takeHandoffSnapshot?.() || {
      position: 0,
      duration: 0,
      wasPlaying: inlinePlayingRef.current,
    };
    const handoff = createPlaybackHandoff({
      entity,
      sample,
      wasPlaying: sample.wasPlaying,
    });

    playYouTube({
      youtube_id: video.youtube_id || videoId,
      title: video.title,
      channel_title: video.channel_title,
      thumbnail: video.thumbnail,
      url: video.url || `https://www.youtube.com/watch?v=${videoId}`,
      type: isVideoMix ? 'video_mix' : 'record',
      playback_handoff: handoff,
    });

    const result = await applyPlayBlockHandoff({ playerRef: ytPlayerRef, handoff });
    setHandoffPending(false);
    if (!result.applied) toast.error('Could not continue in the player. Please try again.');
  };

  const handleAddToQueue = () => {
    if (!video) return;
    addToQueue({
      id: video.youtube_id || videoId,
      track_id: video.youtube_id || videoId,
      track_type: 'youtube',
      youtube_id: video.youtube_id || videoId,
      title: video.title,
      artist: video.channel_title,
      cover: video.thumbnail,
      url: video.url || `https://www.youtube.com/watch?v=${videoId}`,
      video_type: isVideoMix ? 'video_mix' : 'record',
    });
    toast.success('Added to queue');
  };

  const jsonLd = video ? {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title,
    description: video.description?.slice(0, 300) || `${video.title} — ${video.channel_title}`,
    thumbnailUrl: video.thumbnail,
    uploadDate: video.published_at,
    embedUrl: video.embed_url,
    contentUrl: video.url,
    publisher: { '@type': 'Organization', name: video.channel_title },
    interactionStatistic: videoViewCount(video) ? {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/WatchAction',
      userInteractionCount: videoViewCount(video),
    } : undefined,
  } : null;

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 text-primary animate-spin" /></div>;
  if (error || !video) return (
    <div className="min-h-screen py-24 px-4 text-center">
      <h1 className="text-3xl font-bold mb-4">{error || 'Not found'}</h1>
      <button type="button" onClick={fetchVideo} className="text-primary hover:underline mr-5">Try again</button>
      <Link to={backPath} className="text-primary hover:underline">Back to {backLabel}</Link>
    </div>
  );

  const resolvedVideoId = video.youtube_id || videoId;
  const fallbackUrl = video.url || `https://www.youtube.com/watch?v=${resolvedVideoId}`;

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <SEO
        title={video.title}
        description={video.description?.slice(0, 160) || `Watch ${video.title} by ${video.channel_title} on Rastyle Republik`}
        image={video.thumbnail}
        url={isVideoMix ? `/video-mixes/${videoId}` : recordVideoRoute(videoId)}
        type="video.other"
        jsonLd={jsonLd}
      />
      <div className="max-w-5xl mx-auto">
        <Link to={backPath} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors" data-testid="back-to-list">
          <ArrowLeft className="w-4 h-4" /> Back to {backLabel}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
          data-testid="video-embed"
        >
          <InlineYouTubePlayer
            ref={inlinePlayerRef}
            key={resolvedVideoId}
            videoId={resolvedVideoId}
            title={video.title}
            entity={entity}
            metadata={metadata}
            fallbackUrl={fallbackUrl}
            onPlayingChange={handleInlinePlayingChange}
          />
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-3" data-testid="video-title">{video.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
            <span className="font-semibold text-white">{video.channel_title}</span>
            {video.published_at && (
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(video.published_at).toLocaleDateString()}</span>
            )}
            <span className="flex items-center gap-1" data-testid="video-view-count"><Eye className="w-3.5 h-3.5" />{formatVideoViews(video)} YouTube views</span>
            {video.like_count > 0 && (
              <span className="flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" />{video.like_count.toLocaleString()}</span>
            )}
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            <button
              onClick={handlePlayInPlayer}
              disabled={handoffPending}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-black rounded-sm hover:bg-primary/90 disabled:opacity-60 disabled:cursor-wait text-sm font-bold transition-colors neon-gold-shadow"
              data-testid="play-in-player-btn"
              aria-busy={handoffPending}
            >
              {handoffPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Headphones className="w-4 h-4" />}
              {handoffPending ? 'Continuing…' : 'Continue in Player'}
            </button>
            <button
              onClick={handleAddToQueue}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 rounded-sm hover:border-primary text-sm font-medium transition-colors"
              data-testid="add-to-queue-btn"
            >
              <ListPlus className="w-4 h-4" /> Add to Queue
            </button>
            <ShareButton
              title={video.title}
              url={isVideoMix ? `/video-mixes/${videoId}` : recordVideoRoute(videoId)}
              type={isVideoMix ? 'video_mix' : 'video'}
              contentId={resolvedVideoId}
            />
            <a
              href={fallbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-youtube"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-sm hover:bg-red-700 text-sm font-bold"
            >
              <ExternalLink className="w-4 h-4" /> Watch on YouTube
            </a>
          </div>
        </motion.div>

        {video.description && (
          <div className="glass-card rounded-sm p-6 mb-8" data-testid="video-description">
            <h2 className="font-bold text-lg mb-3">Description</h2>
            <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed line-clamp-[12]">{video.description}</p>
          </div>
        )}

        <AdBanner placement="video_detail" />

        {related.length > 0 && (
          <div>
            <h2 className="font-heading text-xl font-bold mb-4" data-testid="related-videos">Related Videos</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {related.map(rv => (
                <Link
                  key={rv.youtube_id}
                  to={isVideoMix ? `/video-mixes/${rv.youtube_id}` : recordVideoRoute(rv.youtube_id)}
                  className="glass-card rounded-sm overflow-hidden group hover:ring-1 hover:ring-primary/50 transition-all"
                >
                  <div className="aspect-video overflow-hidden">
                    <img src={rv.thumbnail} alt={rv.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-2">
                    <h3 className="text-xs font-semibold line-clamp-2">{rv.title}</h3>
                    <p className="text-[10px] text-muted-foreground truncate">{rv.channel_title}</p>
                    <p className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground"><Eye className="h-3 w-3" />{formatVideoViews(rv)} views</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <AdBanner placement="video_detail_bottom" />
      </div>
    </div>
  );
};

export default VideoDetailPage;
